import paths from 'routes/paths';
import routeParams from 'theme/routeParams';

/**
 * Query da carteira que abre o ingresso de um evento ou lugar assim que a
 * lista chega (`/carteira?abrir={slug}`). Usada uma vez: a carteira limpa o
 * parâmetro depois de abrir.
 */
export const WALLET_OPEN_PARAM = 'abrir';

const APP_LINK = /^whizr:\/\/([^/?#]*)([^?#]*)/i;

/**
 * Destino do toque numa notificação, como rota do site.
 *
 * A API grava `data.url` no formato do app (`whizr://...`, ver
 * `NotificationService` na API) e o app tem o `parseWhizrLink` para abrir
 * esses links. Aqui eles viram as páginas equivalentes:
 *
 * - `whizr://evento/{slug}` → `/evento/{slug}`
 * - `whizr://estabelecimento/{slug}` → `/estabelecimento/{slug}`
 * - `whizr://ingressos` → `/carteira`
 * - `whizr://ingressos/{slug}` → `/carteira?abrir={slug}` (o cartão daquele
 *   evento ou lugar; a carteira agrupa por evento, não por pedido)
 *
 * E os destinos do painel do produtor, que só o site tem (o app do comprador
 * nunca lista esses avisos):
 *
 * - `whizr://painel/financeiro` → `/painel/produtor/financeiro`
 * - `whizr://painel/pedidos` → `/painel/produtor/pedidos`
 * - `whizr://painel/eventos` → `/painel/produtor/eventos`
 * - `whizr://painel/eventos/{slug}` → `/painel/produtor/eventos?detail={slug}`
 *   (a página de eventos abre o detalhe daquele evento)
 *
 * `whizr://perfil/{id}` não tem página no site (não existe perfil de outra
 * pessoa, nem lista de amigos), então devolve `null`: a notificação aparece,
 * mas sem toque. Devolve `null` também para link ausente ou desconhecido — a
 * API pode ganhar destinos antes do site.
 */
export function resolveNotificationPath(url) {
    if (typeof url !== 'string') return null;

    const match = url.match(APP_LINK);
    if (!match) return null;

    const [, host, rest] = match;

    let segments;
    try {
        segments = [host, ...rest.split('/')].filter(Boolean).map(decodeURIComponent);
    } catch {
        return null;
    }

    const [kind, slug] = segments;

    switch (kind) {
        case 'evento':
            return slug ? `${paths.events.page}/${encodeURIComponent(slug)}` : null;

        case 'estabelecimento':
            return slug ? `${paths.establishments.page}/${encodeURIComponent(slug)}` : null;

        case 'ingressos':
            return slug
                ? `${paths.user.wallet}?${WALLET_OPEN_PARAM}=${encodeURIComponent(slug)}`
                : paths.user.wallet;

        case 'painel':
            return resolvePanelPath(segments[1], segments[2]);

        default:
            return null;
    }
}

// `section` e `slug` vêm depois de `whizr://painel/`. Seção que o painel não
// tem devolve `null`, como qualquer destino desconhecido.
function resolvePanelPath(section, slug) {
    switch (section) {
        case 'financeiro':
            return paths.dashboard.financial;

        case 'pedidos':
            return paths.dashboard.orders;

        case 'eventos':
            return slug
                ? `${paths.dashboard.events}?${routeParams.resume.detail}=${encodeURIComponent(slug)}`
                : paths.dashboard.events;

        default:
            return null;
    }
}
